import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import cn from 'classnames';
import { NavLink } from 'react-router-dom';
import i18next from 'i18next';

import loginIcon from '~assets/login.png';
import storeIcon from '~assets/store.png';
import ModalActions from '~redux/Modal/actions';
import AuthActions from '~redux/Auth/actions';
import OrderActions from '~redux/Order/actions';
import { MODALS } from '~redux/Modal/constants';
import { CLIENT_PAGES } from '~constants/pages';
import Text from '~components/Text';
import { ORDERS_TABS } from '~constants/orders';
import CustomModal from '~app/components/CustomModal';
import { compareFooterPosition } from '~utils/compare';

import IntercomButton from './components/IntercomButton';
import styles from './styles.module.scss';
import { DEFAULT_USER_MESSAGE } from './constants';

const ProfileMenuModal = () => {
  const dispatch = useDispatch();
  const { profileMenuModalIsOpen } = useSelector((state) => state.modal);
  const { currentUser } = useSelector((state) => state.auth);
  const { pages } = useSelector((state) => state.pages);
  const openLogin = () => dispatch(ModalActions.openModal(MODALS.LOGIN));

  const closeProfileMenu = () => {
    dispatch(ModalActions.closeModal(MODALS.PROFILE_MENU));
  };

  const closeSession = () => {
    dispatch(AuthActions.closeSession());
    closeProfileMenu();
  };

  const handleOrdersClick = () => {
    dispatch(OrderActions.setOrdersTab(ORDERS_TABS.ORDERS_HISTORY));
    closeProfileMenu();
  };

  const sortedPages = pages?.page ? [...pages?.page].sort(compareFooterPosition) : [];
  const pagesToShow = sortedPages.filter(({ published }) => published);
  return (
    <CustomModal
      closeTimeoutMS={300}
      modal={MODALS.PROFILE_MENU}
      className={cn(
        'column full-height',
        {
          [styles.lateralMenu]: profileMenuModalIsOpen
        },
        {
          [styles.out]: !profileMenuModalIsOpen
        }
      )}
      isOpen={profileMenuModalIsOpen}
      defaultClassName={false}>
      <div className="column">
        {!currentUser && (
          <NavLink onClick={openLogin} to="" className="link link-inherit-size">
            <h5 className={`subtitle-small-light ${styles.button}`}>
              <img className={`${styles.navSectionTitleIcon} ${styles.littleIcon}`} src={loginIcon} />
              {i18next.t('CompleteProfile:login')}
            </h5>
          </NavLink>
        )}
        {currentUser && (
          <div className={`row full-width end ${styles.profileName}`}>
            <span className={`${styles.greeting}`}>{i18next.t('ProfileLinks:greeting')}</span>
            <Text className="body-font text-uppercase title-large">
              {currentUser?.name || DEFAULT_USER_MESSAGE}
            </Text>
          </div>
        )}
        <div className={`row full-width ${styles.navLinks}`}>
          <NavLink
            exact
            activeClassName={styles.activeLink}
            onClick={handleOrdersClick}
            to={CLIENT_PAGES.HOME.basePath}
            className={`link link--menu  ${styles.profileItem} ${styles.withHover} link-inherit-size`}>
            <h5 className={`${styles.subtitleSmallBold} ${styles.text}`}>
              {i18next.t('ProfileLinks:start')}
            </h5>
          </NavLink>
          {currentUser && (
            <NavLink
              exact
              activeClassName={styles.activeLink}
              onClick={closeProfileMenu}
              to={CLIENT_PAGES.PROFILE.path}
              className={`link link--menu ${styles.profileItem} ${styles.withHover} link-inherit-size`}>
              <h5 className={`${styles.subtitleSmallBold} ${styles.text}`}>
                {i18next.t('ProfileLinks:myProfile')}
              </h5>
            </NavLink>
          )}
          {currentUser && (
            <NavLink
              exact
              activeClassName={styles.activeLink}
              onClick={handleOrdersClick}
              to={CLIENT_PAGES.ORDERS.path}
              className={`link link--menu ${styles.profileItem} ${styles.withHover} link-inherit-size`}>
              <h5 className={`${styles.subtitleSmallBold} ${styles.text}`}>
                {i18next.t('ProfileLinks:myOrders')}
              </h5>
            </NavLink>
          )}
          <IntercomButton
            className={`link link--menu ${styles.profileItem} ${styles.withHover} link-inherit-size`}
            iconClassName={`m-right-2 ${styles.icon} text-emperor`}
            textClassName={`${styles.text} ${styles.subtitleSmallBold}`}
          />
          {currentUser && (
            <button
              type="button"
              onClick={closeSession}
              className={`link link--menu link-inherit-size ${styles.withHover}`}>
              <h5 className={`${styles.subtitleSmallBold} ${styles.text}`}>
                {i18next.t('ProfileLinks:closeSession')}
              </h5>
            </button>
          )}
        </div>
      </div>
      <hr className={styles.navSeparator} />
      <div className="column">
        <div className="m-bottom-1 p-left-10">
          <img className={`${styles.navSectionTitleIcon} ${styles.mediumIcon}`} src={storeIcon} />
          <h5 className={styles.navSectionTitleText}>{i18next.t('ProfileLinks:knowUs')}</h5>
        </div>
        <div className={`row full-width ${styles.navLinks}`}>
          {pagesToShow.map(({ id, title, relativeUrl }) => (
            <NavLink
              key={id}
              onClick={closeProfileMenu}
              to={relativeUrl}
              className="link link--menu link-inherit-size">
              <h5 className={`subtitle-small-light ${styles.text}`}>{title}</h5>
            </NavLink>
          ))}
        </div>
      </div>
    </CustomModal>
  );
};

export default ProfileMenuModal;
